<template>
  <v-container class="modern-detail-view pb-12">
    <v-skeleton-loader v-if="loading" type="article, stats, card, card, table" class="modern-skeleton" />
    
    <div v-if="!loading">
      <!-- Premium Detail Header (Detail Pattern) -->
      <v-row class="mb-6 align-center px-4 pt-4">
        <v-btn icon @click="$router.go(-1)" class="mr-4 modern-back-btn bg-white shadow-sm">
          <v-icon small>fas fa-chevron-left</v-icon>
        </v-btn>
        <div class="d-flex align-center">
          <v-avatar color="primary" size="44" class="mr-4 shadow-sm border-white-sm">
            <v-icon color="white" small>fas fa-box-open</v-icon>
          </v-avatar>
          <div>
            <div class="d-flex align-center gap-3">
              <v-chip color="primary" label small class="font-weight-black px-3">{{article.sku}}</v-chip>
              <div class="text-h4 font-weight-black primary--text lh-1">{{article.name}}</div>
            </div>
            <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">Ficha técnica detallada y movimientos de inventario</div>
          </div>
        </div>
        <v-spacer></v-spacer>
        <div class="d-flex align-center gap-3">
          <v-chip outlined color="grey" class="font-weight-bold uppercase">{{article.unit_name}}</v-chip>
        </div>
      </v-row>

      <!-- Quick Metrics Row -->
      <v-row class="mb-8 px-2">
        <v-col cols="12" md="4">
          <v-card outlined class="modern-card metric-card shadow-sm border-left-primary pa-4">
            <div class="d-flex align-center justify-space-between mb-2">
              <span class="caption grey--text font-weight-black text-uppercase tracking-widest">Existencias Totales</span>
                <v-menu v-if="article.stocks" bottom left offset-y transition="slide-y-transition" content-class="modern-card existence-menu">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon x-small v-bind="attrs" v-on="on"><v-icon small>fas fa-warehouse</v-icon></v-btn>
                  </template>
                  <v-card flat class="pa-0">
                    <div class="px-4 py-2 border-bottom bg-lightgrey flex justify-space-between align-center">
                      <span class="caption font-weight-black uppercase tracking-widest grey--text text--darken-2">Stock por Bodega</span>
                    </div>
                    <v-simple-table dense class="modern-simple-table" v-if="article.stocks.length > 0">
                      <tbody>
                        <tr v-for="register in article.stocks" :key="register.id">
                          <td class="caption">{{register.store_name}}</td>
                          <td class="text-end font-weight-black">{{register.qty | number('0,0.00')}}</td>
                        </tr>
                      </tbody>
                    </v-simple-table>
                  </v-card>
                </v-menu>
            </div>
            <div class="d-flex align-baseline">
              <span class="text-h3 font-weight-black primary--text mr-1">{{totalStock(article) | number('0,0.00')}}</span>
              <span class="caption grey--text font-weight-bold uppercase">{{article.unit_name}}</span>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" md="4">
          <v-card outlined class="modern-card metric-card shadow-sm border-left-info pa-4">
            <div class="d-flex align-center justify-space-between mb-2">
              <span class="caption grey--text font-weight-black text-uppercase tracking-widest">Costo Promedio</span>
              <v-icon color="info lighten-3" small>fas fa-calculator</v-icon>
            </div>
            <div class="d-flex align-baseline">
              <span class="text-h3 font-weight-black info--text">{{article.avg | moneda}}</span>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" md="4">
          <v-card outlined class="modern-card metric-card shadow-sm border-left-success pa-4">
            <div class="d-flex align-center justify-space-between mb-2">
              <span class="caption grey--text font-weight-black text-uppercase tracking-widest">Valorización Total</span>
              <v-icon color="success lighten-3" small>fas fa-sack-dollar</v-icon>
            </div>
            <div class="d-flex align-baseline">
              <span class="text-h3 font-weight-black success--text">{{article.avg * totalStock(article) | moneda}}</span>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Details Section -->
      <v-row class="mb-8">
        <v-col cols="12" md="7">
          <v-card outlined class="modern-card shadow-sm overflow-hidden h-100">
            <v-sheet color="primary" class="px-6 py-3 d-flex align-center">
              <v-icon color="white" x-small class="mr-2">fas fa-info-circle</v-icon>
              <span class="caption white--text font-weight-black text-uppercase tracking-widest">Información General</span>
            </v-sheet>
            <div class="pa-6">
              <v-row dense>
                <v-col cols="6" class="mb-4">
                  <div class="caption grey--text font-weight-bold text-uppercase mb-1">Familia / Grupo</div>
                  <div class="body-1 font-weight-black primary--text">{{article.family_name}}</div>
                </v-col>
                <v-col cols="6" class="mb-4">
                  <div class="caption grey--text font-weight-bold text-uppercase mb-1">Unidad de Compra</div>
                  <div class="body-1 font-weight-black primary--text">{{article.unit_name}}</div>
                </v-col>
                <v-col cols="6" class="mb-4">
                  <div class="caption grey--text font-weight-bold text-uppercase mb-1">Stock Crítico</div>
                  <div class="body-1 font-weight-black error--text">{{article.critic | number('0,0.00')}}</div>
                </v-col>
                <v-col cols="6" class="mb-4">
                  <div class="caption grey--text font-weight-bold text-uppercase mb-1">Última Compra</div>
                  <div class="body-1 font-weight-black">{{article.last_buy_date | moment('DD/MM/YYYY')}}</div>
                </v-col>
                <v-col cols="12">
                  <div class="caption grey--text font-weight-bold text-uppercase mb-1">Observaciones</div>
                  <div class="body-2 grey--text text--darken-2 font-italic">{{article.notes || 'Sin observaciones registradas'}}</div>
                </v-col>
              </v-row>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" md="5">
          <v-card outlined class="modern-card shadow-sm overflow-hidden h-100">
            <v-sheet color="secondary" class="px-6 py-3 d-flex align-center">
              <v-icon color="white" x-small class="mr-2">fas fa-chart-line</v-icon>
              <span class="caption white--text font-weight-black text-uppercase tracking-widest">Valorización</span>
            </v-sheet>
            <div class="pa-6">
              <div class="d-flex justify-space-between mb-4 pb-2 border-bottom">
                <span class="body-2 grey--text">Costo Promedio</span>
                <span class="body-2 font-weight-black">{{article.avg | moneda}}</span>
              </div>
              <div class="d-flex justify-space-between mb-4 pb-2 border-bottom">
                <span class="body-2 grey--text">Precio Última Compra</span>
                <span class="body-2 font-weight-black">{{article.last_buy_price | moneda}}</span>
              </div>
              <div class="d-flex justify-space-between mt-6 bg-lightgrey pa-4 rounded-lg">
                <span class="subtitle-1 font-weight-bold primary--text">Valor Inventario</span>
                <span class="subtitle-1 font-weight-black primary--text">{{article.avg * totalStock(article) | moneda}}</span>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Charts Section -->
      <v-card outlined class="modern-card shadow-sm pa-6 mb-8 mt-4">
        <div class="d-flex align-center justify-space-between mb-6">
          <div class="d-flex align-center">
            <v-icon color="primary" small class="mr-2">fas fa-chart-area</v-icon>
            <span class="text-h6 font-weight-black primary--text text-uppercase tracking-wider">Tendencias y Consumos</span>
          </div>
          <v-btn-toggle v-model="optionSelected" mandatory dense color="primary" class="modern-toggle" @change="chartDataFetch">
            <v-btn v-for="data in dataRange" :key="data.text" :value="data" x-small class="px-4 font-weight-black">
              {{data.text}}
            </v-btn>
          </v-btn-toggle>
        </div>
        <v-row v-if="priceDataCollection.labels.length > 0 && outDataCollection.labels.length > 0">
          <v-col cols="12" md="6">
            <div class="caption font-weight-black grey--text text-uppercase tracking-widest mb-4 text-center">Variación de Precio Promedio</div>
            <article-price-graph :datacollection="priceDataCollection" />
          </v-col>
          <v-col cols="12" md="6">
            <div class="caption font-weight-black grey--text text-uppercase tracking-widest mb-4 text-center">Histórico de Salidas</div>
            <article-out-graph :datacollection="outDataCollection" />
          </v-col>
        </v-row>
        <v-row v-else-if="!chartDataLoading" class="py-12 justify-center">
          <div class="text-center">
            <v-icon color="grey lighten-2" size="48" class="mb-2">fas fa-chart-bar</v-icon>
            <div class="caption grey--text font-weight-bold uppercase">No hay datos suficientes para generar gráficos</div>
          </div>
        </v-row>
        <v-row v-else class="py-12 justify-center">
          <v-progress-circular indeterminate color="primary" />
        </v-row>
      </v-card>

      <!-- Movements Section (Bank Statement Style) -->
      <v-card outlined class="modern-card overflow-hidden shadow-sm">
        <v-sheet color="#f8fbfc" class="px-6 py-4 d-flex align-center border-bottom">
          <v-icon color="primary" small class="mr-2">fas fa-history</v-icon>
          <span class="text-h6 font-weight-black primary--text text-uppercase tracking-wider flex-grow-1">Estado de Cuenta / Movimientos</span>
          <div class="d-flex gap-3">
             <v-text-field v-model="filter.created_after" type="date" label="Desde" outlined dense hide-details class="modern-input white bg-white" style="width: 160px" />
             <v-text-field v-model="filter.created_before" type="date" label="Hasta" outlined dense hide-details class="modern-input white bg-white" style="width: 160px" />
          </div>
        </v-sheet>

        <v-data-table
          :headers="headers"
          :items="transactions"
          :items-per-page="-1"
          :loading="transactionLoading"
          class="modern-table statement-table"
          hide-default-footer
          :item-class="itemRowStyle"
        >
          <template v-slot:body.prepend>
            <tr class="balance-row">
              <td colspan="6" class="text-end caption font-weight-black grey--text text-uppercase tracking-widest">Saldo Inicial</td>
              <td></td><td></td><td></td><td></td>
              <td class="text-end font-weight-black primary--text">{{initial_balance | number('0,0.00')}}</td>
            </tr>
          </template>

          <template v-slot:item.created_at="{ item }">
            <div class="caption font-weight-bold grey--text">{{item.created_at | moment('DD/MM/YYYY')}}</div>
          </template>

          <template v-slot:item.created_by_full_name="{ item }">
             <div class="caption font-weight-medium">{{item.created_by_full_name}}</div>
          </template>

          <template v-slot:item.tipo="{ item }">
            <div class="d-flex align-center">
              <v-icon x-small :color="item.tipo_in_out ? 'success' : 'error'" class="mr-2">
                {{item.tipo_in_out ? 'fas fa-arrow-right-to-bracket' : 'fas fa-arrow-right-from-bracket'}}
              </v-icon>
              <span class="caption font-weight-black text-uppercase">{{item.tipo_name}}</span>
            </div>
          </template>

          <template v-slot:item.qty_in="{ item }">
             <div v-if="item.tipo_in_out" class="font-weight-black success--text">+{{item.qty | number('0,0.00')}}</div>
          </template>

          <template v-slot:item.qty_out="{ item }">
             <div v-if="!item.tipo_in_out" class="font-weight-black error--text">-{{item.qty | number('0,0.00')}}</div>
          </template>

          <template v-slot:item.price="{ item }">
            <span class="caption font-weight-bold">{{item.price | moneda}}</span>
          </template>

          <template v-slot:item.total="{ item }">
            <span :class="item.tipo_in_out ? 'success--text' : 'error--text'" class="font-weight-black">
               {{ (item.tipo_in_out ? 1 : -1) * item.price * item.qty | moneda }}
            </span>
          </template>

          <template v-slot:item.balance="{ item }">
             <div class="font-weight-black primary--text">{{item.balance | number('0,0.00')}}</div>
          </template>

          <template v-slot:body.append>
            <tr class="balance-row">
              <td colspan="6" class="text-end caption font-weight-black grey--text text-uppercase tracking-widest">Saldo Final</td>
              <td></td><td></td><td></td><td></td>
              <td class="text-end font-weight-black primary--text text-h6">{{final_balance | number('0,0.00')}}</td>
            </tr>
          </template>
        </v-data-table>
      </v-card>
    </div>
  </v-container>
</template>
<script>
import {getArticle,fetchTransactions,fetchStores} from '@/api/stocks'
import ArticlePriceGraph from '@/components/stocks/ArticlePriceGraph'
import ArticleOutGraph from '@/components/stocks/ArticleOutGraph'
import NumberLabel from '@/components/utils/NumberLabel'
import moment from 'moment'

export default {
  name:'DetailedArticle',
  components:{NumberLabel,ArticlePriceGraph,ArticleOutGraph},
  async mounted(){  
    if(this.$route.params.id){
      await getArticle(this.$route.params.id)
      .then((data)=>{
        this.article=data
        this.loading=false
      })
      .catch((err)=>notifications.showError('Error Cargando Artículo' + err))
      this.fetch()
      
      //this.stores=await fetchStores()

      //Retreive chart Data
      this.optionSelected = this.dataRange[0]
      await this.chartDataFetch()
    }
  },
  data() {
    return {
      loading:false,
      transactionLoading:false,
      article:'',
      transactions:[],
      stores:[],
      store:'',
      initial_balance:0,
      final_balance:0,
      headers:[
      { text: 'Creada', value: 'created_at' ,class:'table-header',sortable:false,},
      { text: 'Usuario', value: 'created_by_full_name' ,class:'table-header',sortable:false,},
      { text: 'Ref.', value: 'notes',class:'table-header',sortable:false},
      { text: 'Centro', value: 'center_name',class:'table-header',sortable:false},
      { text: 'Bodega', value: 'store_name',class:'table-header',sortable:false},
      { text: 'Movimiento', value: 'tipo',class:'table-header' ,sortable:false,},
      { text: 'Entrada', value: 'qty_in',class:'table-header',sortable:false,align:'end' },
      { text: 'Salida', value: 'qty_out',class:'table-header' ,sortable:false,align:'end'},
      { text: 'Valor', value: 'price',class:'table-header',sortable:false,align:'end'},
      { text: 'Total', value: 'total',class:'table-header',sortable:false,align:'end'},
      {text:'Balance',value:'balance',class:'table-header',sortable:false,align:'end'}
    ],
    filter:{
      created_after:moment(new Date(new Date().getFullYear(), new Date().getMonth(), 1)).format('yyyy-MM-DD'),
      created_before:'',
      store:''
    },
    search:'',
    //Chart Feed Data 
    dataRange:[
        {
        text:  moment(new Date().toString()).format('MMMM-YYYY'),
        after: moment(new Date().toString()).format('YYYY')+'-'+moment(new Date().toString()).format('MM')+'-01',
        before:moment(new Date().toString()).format('YYYY-MM-DD'),
        format:'DD-MM-YYYY',
        },
        {
          text:'30 días',
          after: moment(new Date().toString()).add(-1,'month').format('YYYY-MM-DD'),
          before:moment(new Date().toString()).format('YYYY-MM-DD'),
          format:'DD-MM-YYYY',
        },
        {
          text:'12 Meses',
          after:moment(new Date().toString()).add(-1,'year').format('YYYY-MM-DD'),
          before:moment(new Date().toString()).format('YYYY-MM-DD'),
          format:'MMM',
        }
      ],
      optionSelected:{},
      priceDataCollection:{labels:[], datasets:[]},
      outDataCollection:{labels:[], datasets:[]},
      chartDataLoading:false,
    }

  },
  methods:{
    totalStock(item){
      if(item){
        if(this.store){
          const filteredStocks = item.stocks.filter(i=>i.store==this.store)
          return filteredStocks.reduce((acc, val)=>{ return acc + parseInt(val.qty); }, 0)
        }
        else return item.stocks.reduce((acc, val)=>{ return acc + parseInt(val.qty); }, 0)
      }
      else return 0
    },
    async fetch(){
      this.transactionLoading=true
      this.initial_balance=0
      this.final_balance=0
      //Arreglando variables de fecha en caso de ser null
      if(!this.filter.created_after) this.filter.created_after=''
      if(!this.filter.created_before) this.filter.created_before=''
      if (!this.filter.store) this.filter.store=''
      const query=`tarticle=${this.$route.params.id}&store=${this.filter.store}&created_after=${this.filter.created_after}&created_before=${this.filter.created_before}`
      await fetchTransactions(query)
      .then(res=>{
        const data=res.results
        this.transactions=data
        if(data[0]) {
          this.initial_balance=data[0].initial_qty
          this.final_balance=data[data.length-1].balance
        }
        this.transactionLoading=false
      })
    },
    searchTimeOut() {  
      //Función para hacer búsqueda lazy
      if (this.timer) {
          clearTimeout(this.timer);
          this.timer = null;
      }
      this.timer = setTimeout(() => {
          this.fetch(this.$route.params.id)
      }, 800);
    },
    itemRowStyle(item){
      return item.tipo_in_out&&item.tipo_price_effect ? 'font-weight-black':''
    },
    async chartDataFetch(){
      this.chartDataLoading = true
      const query = `tarticle=${this.$route.params.id}&created_after=${this.optionSelected.after}&created_before=${this.optionSelected.before}`
      
      try {
        const res = await fetchTransactions(query)
        if (res.results && res.results.length > 0) {
          // Data for Price Chart
          this.priceDataCollection = {
            labels: res.results.map(d => moment(d.created_at).format('DD/MM/YY')),
            datasets: [
              {
                label: 'Precio Promedio',
                borderColor: '#1A1F2E',
                backgroundColor: 'rgba(26, 31, 46, 0.05)',
                data: res.results.map(d => d.calculated_avg || 0)
              }
            ]
          }

          // Data for Out Chart
          const format = this.optionSelected.format
          const onlyOut = res.results.filter(t => !t.tipo_in_out)
          const results_by_Date = onlyOut.reduce((r, a) => {
            const key = moment(a.created_at).format(format)
            r[key] = r[key] || []
            r[key].push(a)
            return r
          }, Object.create(null))

          const outDataResults = Object.keys(results_by_Date).map(date => ({
            date: date,
            qty: results_by_Date[date].reduce((acc, val) => acc + parseFloat(val.qty), 0)
          }))

          this.outDataCollection = {
            labels: outDataResults.map(d => d.date),
            datasets: [
              {
                label: 'Salidas',
                data: outDataResults.map(d => d.qty),
                backgroundColor: '#1A1F2E',
                borderColor: '#1A1F2E'
              }
            ]
          }
        } else {
          this.priceDataCollection = {labels:[], datasets:[]}
          this.outDataCollection = {labels:[], datasets:[]}
        }
      } catch (error) {
        console.error('Error fetching chart data:', error)
      } finally {
        this.chartDataLoading = false
      }
    }
  },
  watch:{
    filter:{
      deep: true,
      handler:'fetch'
    }
  }

}
</script>

<style scoped>
.modern-detail-view { background-color: #f4f7f9; min-height: 100vh; }
.modern-card { border-radius: 12px !important; border: 1px solid rgba(0,0,0,0.05) !important; background-color: white !important; }
.shadow-sm { box-shadow: 0 2px 12px rgba(0,0,0,0.04) !important; }
.border-white-sm { border: 2px solid white !important; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.05) !important; }
.bg-lightgrey { background-color: #f8fbfc; }
.lh-1 { line-height: 1 !important; }
.gap-3 { gap: 12px; }
.uppercase { text-transform: uppercase; }
.tracking-widest { letter-spacing: 2px; }
.tracking-wider { letter-spacing: 1px; }

.modern-back-btn { transition: transform 0.2s; &:hover { transform: translateX(-4px); background-color: #f5f5f5 !important; } }

.metric-card {
  transition: transform 0.2s;
  &:hover { transform: translateY(-4px); }
  &.border-left-primary { border-left: 4px solid var(--v-primary-base) !important; }
  &.border-left-info { border-left: 4px solid #00bcd4 !important; }
  &.border-left-success { border-left: 4px solid #4CAF50 !important; }
}

.modern-input >>> .v-input__control fieldset { border-radius: 8px !important; border-color: #e0e4e8 !important; }

.modern-table >>> .v-data-table-header th { white-space: nowrap; height: 50px !important; text-transform: uppercase; font-size: 0.65rem !important; letter-spacing: 1.5px; font-weight: 900 !important; background-color: #f8fbfc !important; color: #607d8b !important; }
.modern-table >>> td { padding: 12px 16px !important; border-bottom: 1px solid #f0f3f5 !important; }

.statement-table >>> .v-data-table__wrapper { border-radius: 0 0 12px 12px; }
.statement-table >>> tr:hover { background-color: rgba(var(--v-primary-base), 0.02) !important; }

.balance-row { background-color: #fcfdfe !important; height: 64px; }
.balance-row td { border-top: 2px solid #eee !important; border-bottom: 2px solid #eee !important; }

.modern-toggle >>> .v-btn { border-color: rgba(0,0,0,0.05) !important; text-transform: none; }
.modern-toggle >>> .v-btn--active { background-color: var(--v-primary-base) !important; color: white !important; }

.existence-menu { border-radius: 12px !important; overflow: hidden; box-shadow: 0 8px 24px rgba(0,0,0,0.12) !important; border: 1px solid rgba(0,0,0,0.08) !important; }
.modern-simple-table { background: transparent !important; }
.modern-simple-table >>> th { font-size: 0.65rem !important; text-transform: uppercase; letter-spacing: 1px; color: #90a4ae !important; height: 32px !important; }
.modern-simple-table >>> td { font-size: 0.8rem !important; height: 36px !important; }

.modern-skeleton { background: transparent !important; }
</style>
